/**
 * Garmin-Style Marine Gauge Components
 * Professional marine gauges with Garmin-style aesthetics and functionality
 */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../../styles/theme';

// Circular Gauge Container
const GaugeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: 200px;
  background: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.lg};
  padding: 1rem;
  position: relative;
`;

const GaugeTitle = styled.div`
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CircularGaugeSVG = styled.svg`
  width: 160px;
  height: 160px;
  transform: rotate(-90deg);
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
`;

const GaugeBackground = styled.circle`
  fill: none;
  stroke: ${theme.colors.border.secondary};
  stroke-width: 8;
  opacity: 0.3;
`;

const GaugeZone = styled.path`
  fill: none;
  stroke-width: 8;
  stroke-linecap: round;
  transition: all 0.3s ease;
`;

const GaugeNeedle = styled.line`
  stroke: #ffffff;
  stroke-width: 3;
  stroke-linecap: round;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
`;

const CenterDot = styled.circle`
  fill: ${theme.colors.text.primary};
  stroke: #ffffff;
  stroke-width: 2;
`;

const DigitalOverlay = styled.div`
  position: absolute;
  bottom: 30%;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: #00ff00;
  padding: 0.25rem 0.75rem;
  border-radius: ${theme.borderRadius.md};
  font-family: 'Monaco', 'Consolas', monospace;
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.bold};
  text-align: center;
  min-width: 80px;
  border: 1px solid rgba(0, 255, 0, 0.3);
`;

const UnitLabel = styled.div`
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.text.secondary};
  margin-top: 0.25rem;
  text-align: center;
`;

// Utility functions for gauge calculations
const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
  const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
  return {
    x: centerX + (radius * Math.cos(angleInRadians)),
    y: centerY + (radius * Math.sin(angleInRadians))
  };
};

const describeArc = (x, y, radius, startAngle, endAngle) => {
  const start = polarToCartesian(x, y, radius, endAngle);
  const end = polarToCartesian(x, y, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
  return [
    "M", start.x, start.y, 
    "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y
  ].join(" ");
};

// Garmin-style color zones
const getZoneColor = (zone) => {
  switch (zone) {
    case 'normal': return '#00ff00';    // Bright green
    case 'caution': return '#ffff00';   // Bright yellow  
    case 'warning': return '#ff8800';   // Orange
    case 'alarm': return '#ff0000';     // Bright red
    default: return '#00ff00';
  }
};

/**
 * Garmin-Style Circular Gauge Component
 */
export const GarminCircularGauge = ({ 
  title = "Gauge",
  value = 0,
  min = 0,
  max = 100,
  unit = "",
  zones = [
    { min: 0, max: 70, zone: 'normal' },
    { min: 70, max: 85, zone: 'caution' },
    { min: 85, max: 95, zone: 'warning' },
    { min: 95, max: 100, zone: 'alarm' }
  ],
  precision = 1,
  startAngle = -135,
  endAngle = 135
}) => {
  const [displayValue, setDisplayValue] = useState(0);
  const [currentZone, setCurrentZone] = useState('normal');

  // Smooth value animation
  useEffect(() => {
    const timer = setTimeout(() => {
      const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
      setDisplayValue(numValue);
    }, 100);
    return () => clearTimeout(timer);
  }, [value]);

  // Determine current zone
  useEffect(() => {
    const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
    const zone = zones.find(z => numValue >= z.min && numValue <= z.max);
    setCurrentZone(zone ? zone.zone : 'normal');
  }, [value, zones]);

  const radius = 70;
  const centerX = 80;
  const centerY = 80;
  
  // Calculate needle angle
  const numDisplayValue = typeof displayValue === 'number' ? displayValue : parseFloat(displayValue) || 0;
  const valuePercent = Math.max(0, Math.min(1, (numDisplayValue - min) / (max - min)));
  const needleAngle = startAngle + (valuePercent * (endAngle - startAngle));
  
  // Calculate needle position
  const needleEnd = polarToCartesian(centerX, centerY, radius - 10, needleAngle);

  return (
    <GaugeContainer>
      <GaugeTitle>{title}</GaugeTitle>
      
      <div style={{ position: 'relative' }}>
        <CircularGaugeSVG viewBox="0 0 160 160">
          {/* Background circle */}
          <GaugeBackground
            cx={centerX}
            cy={centerY}
            r={radius}
          />
          
          {/* Zone arcs */}
          {zones.map((zone, index) => {
            const zoneStartPercent = (zone.min - min) / (max - min);
            const zoneEndPercent = (zone.max - min) / (max - min);
            const zoneStartAngle = startAngle + (zoneStartPercent * (endAngle - startAngle));
            const zoneEndAngle = startAngle + (zoneEndPercent * (endAngle - startAngle));
            
            return (
              <GaugeZone
                key={index}
                d={describeArc(centerX, centerY, radius, zoneStartAngle, zoneEndAngle)}
                stroke={getZoneColor(zone.zone)}
                opacity={currentZone === zone.zone ? 1 : 0.6}
              />
            );
          })}
          
          {/* Needle */}
          <GaugeNeedle
            x1={centerX}
            y1={centerY}
            x2={needleEnd.x}
            y2={needleEnd.y}
          />
          
          {/* Center dot */}
          <CenterDot
            cx={centerX}
            cy={centerY}
            r={4}
          />
        </CircularGaugeSVG>
        
        {/* Digital overlay */}
        <DigitalOverlay>
          {numDisplayValue.toFixed(precision)}
          {unit && <UnitLabel>{unit}</UnitLabel>}
        </DigitalOverlay>
      </div>
    </GaugeContainer>
  );
};

/**
 * RPM Gauge - Specialized for engine RPM
 */
export const GarminRPMGauge = ({ value = 0, max = 4000 }) => (
  <GarminCircularGauge
    title="Engine RPM"
    value={value}
    min={0}
    max={max}
    unit="RPM"
    precision={0}
    zones={[
      { min: 0, max: max * 0.7, zone: 'normal' },
      { min: max * 0.7, max: max * 0.85, zone: 'caution' },
      { min: max * 0.85, max: max * 0.95, zone: 'warning' },
      { min: max * 0.95, max: max, zone: 'alarm' }
    ]}
  />
);

/**
 * Speed Gauge - For vessel speed
 */
export const GarminSpeedGauge = ({ value = 0, max = 25, unit = "kts" }) => (
  <GarminCircularGauge
    title="Speed"
    value={value}
    min={0}
    max={max}
    unit={unit}
    precision={1}
    zones={[
      { min: 0, max: max, zone: 'normal' }
    ]}
  />
);

/**
 * Temperature Gauge - For engine or other temperatures
 */
export const GarminTemperatureGauge = ({ 
  title = "Temperature",
  value = 0, 
  min = 100, 
  max = 220, 
  unit = "°F" 
}) => (
  <GarminCircularGauge
    title={title}
    value={value}
    min={min}
    max={max}
    unit={unit}
    precision={0}
    zones={[
      { min: min, max: min + (max - min) * 0.7, zone: 'normal' },
      { min: min + (max - min) * 0.7, max: min + (max - min) * 0.85, zone: 'caution' },
      { min: min + (max - min) * 0.85, max: min + (max - min) * 0.95, zone: 'warning' },
      { min: min + (max - min) * 0.95, max: max, zone: 'alarm' }
    ]}
  />
);

/**
 * Voltage Gauge - For electrical systems
 */
export const GarminVoltageGauge = ({ value = 12.0, min = 10, max = 15 }) => (
  <GarminCircularGauge
    title="Battery Voltage"
    value={value}
    min={min}
    max={max}
    unit="V"
    precision={1}
    zones={[
      { min: min, max: 11.8, zone: 'alarm' },
      { min: 11.8, max: 12.2, zone: 'warning' },
      { min: 12.2, max: 14.5, zone: 'normal' },
      { min: 14.5, max: max, zone: 'caution' }
    ]}
  />
);

export default {
  GarminCircularGauge,
  GarminRPMGauge,
  GarminSpeedGauge,
  GarminTemperatureGauge,
  GarminVoltageGauge
};